/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {GetterFn, MethodFn, ReflectionCapabilities, SetterFn, reflector} from '../private_import_core';
import {StaticReflector} from './static_reflector';
import {StaticSymbol} from './static_symbol';

export class StaticAndDynamicReflectionCapabilities {
/**
 * @param {?} staticDelegate
 * @return {?}
 */
static install(staticDelegate: StaticReflector) {
    reflector.updateCapabilities(new StaticAndDynamicReflectionCapabilities(staticDelegate));
  }
private dynamicDelegate = new ReflectionCapabilities();
/**
 * @param {?} staticDelegate
 */
constructor(private staticDelegate: StaticReflector) {}
/**
 * @return {?}
 */
isReflectionEnabled(): boolean { return true; }
/**
 * @param {?} type
 * @return {?}
 */
factory(type: any): Function { return this.dynamicDelegate.factory(type); }
/**
 * @param {?} type
 * @param {?} lcProperty
 * @return {?}
 */
hasLifecycleHook(type: any, lcProperty: string): boolean {
    return isStaticType(type) ? this.staticDelegate.hasLifecycleHook(type, lcProperty) :
                                this.dynamicDelegate.hasLifecycleHook(type, lcProperty);
  }
/**
 * @param {?} type
 * @return {?}
 */
parameters(type: any): any[][] {
    return isStaticType(type) ? this.staticDelegate.parameters(type) :
                                this.dynamicDelegate.parameters(type);
  }
/**
 * @param {?} type
 * @return {?}
 */
annotations(type: any): any[] {
    return isStaticType(type) ? this.staticDelegate.annotations(type) :
                                this.dynamicDelegate.annotations(type);
  }
/**
 * @param {?} typeOrFunc
 * @return {?}
 */
propMetadata(typeOrFunc: any): {[key: string]: any[]} {
    return isStaticType(typeOrFunc) ? this.staticDelegate.propMetadata(typeOrFunc) :
                                      this.dynamicDelegate.propMetadata(typeOrFunc);
  }
/**
 * @param {?} name
 * @return {?}
 */
getter(name: string): GetterFn { return this.dynamicDelegate.getter(name); }
/**
 * @param {?} name
 * @return {?}
 */
setter(name: string): SetterFn { return this.dynamicDelegate.setter(name); }
/**
 * @param {?} name
 * @return {?}
 */
method(name: string): MethodFn { return this.dynamicDelegate.method(name); }
/**
 * @param {?} type
 * @return {?}
 */
importUri(type: any): string { return this.staticDelegate.importUri(type); }
/**
 * @param {?} name
 * @param {?} moduleUrl
 * @param {?} runtime
 * @return {?}
 */
resolveIdentifier(name: string, moduleUrl: string, runtime: any) {
    return this.staticDelegate.resolveIdentifier(name, moduleUrl);
  }
/**
 * @param {?} enumIdentifier
 * @param {?} name
 * @return {?}
 */
resolveEnum(enumIdentifier: any, name: string): any {
    if (isStaticType(enumIdentifier)) {
      return this.staticDelegate.resolveEnum(enumIdentifier, name);
    } else {
      return null;
    }
  }
}

function StaticAndDynamicReflectionCapabilities_tsickle_Closure_declarations() {
 /** @type {?} */
StaticAndDynamicReflectionCapabilities.prototype.dynamicDelegate;
 /** @type {?} */
StaticAndDynamicReflectionCapabilities.prototype.staticDelegate;
}

/**
 * @param {?} type
 * @return {?}
 */
function isStaticType(type: any): boolean {
  return typeof type === 'object' && type.name && type.filePath;
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
